import React from 'react'
import styled, { css, withTheme } from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import { H1, H4, P } from '@jarvis.oddle/core/components/Typo'
import {
  colors,
  margin,
  marginSizes,
  padding,
  paddingSizes,
  promotionFilmStripSection
} from '@jarvis.oddle/core/theme/default'
import {
  promotionAppliedOnTypes,
  promotionTypes
} from '@jarvis.oddle/core/tools/promotion'
import { amountTypes } from '@jarvis.oddle/core/tools/number'
import IconBack from '@jarvis.oddle/core/components/Icons/IconBack'
import { DefaultButtonText } from '@jarvis.oddle/core/components/Button'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { useAppContext } from 'src/containers/AppContext'

const promotionCardHeightStyle = css<{
  showDefaultImage: boolean
}>`
  height: ${(props) =>
    props.showDefaultImage
      ? promotionFilmStripSection.mobileHeight
      : promotionFilmStripSection.mobileHideHeight};
  ${breakpoint('desktop')`
    height: ${(props) =>
      props.showDefaultImage
        ? promotionFilmStripSection.desktopHeight
        : promotionFilmStripSection.desktopHideHeight};
  `}
`

const PromotionCardContainer = styled(Card)`
  padding: 0;
  width: 100%;
  overflow: hidden;
  white-space: normal;
  ${promotionCardHeightStyle}
  ${breakpoint('desktop')`
    padding: 0;
  `}
`

const PromotionCardWrapper = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  height: '100%'
}))`
  &:hover {
    cursor: pointer;
  }
`

const PromotionBanner = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
}))`
  flex: 0 0 100px;
  background-color: ${(props) =>
    props.theme.buttonColor || colors.brandPrimary};
  color: ${(props) => props.theme.buttonTextColor || colors.buttonTextColor};
  ${breakpoint('desktop')`
    flex: 0 0 120px;
  `}
`

const PromotionContent = styled(Flex).attrs(() => ({
  justifyContent: 'space-between',
  flexDirection: 'column'
}))`
  padding: ${padding[paddingSizes.medium]};
  flex: 0 1 100%;
  width: 100%;
`

const PromotionText = styled(H4).attrs(() => ({
  textAlign: 'left',
  normalised: true,
  line: 2
}))`
  ${(props) => (props.color ? `color: ${props.color};` : '')}
`

export const DefaultPromotionBannerHeader = styled(H1).attrs(() => ({
  normalised: true,
  bold: true,
  textAlign: 'center'
}))`
  && {
    ${(props) => (props.color ? `color: ${props.color}` : 'color: inherit')};
    font-size: 30px;
    ${breakpoint('desktop')`
      font-size: 38px;
    `}
  }
`

export const DefaultPromotionBannerSubHeader = styled(P).attrs(() => ({
  normalised: true
}))`
  && {
    color: inherit;
    margin-top: ${margin[marginSizes.none]};
    ${breakpoint('desktop')`
      margin-top: ${margin[marginSizes.small]};
    `}
  }
`

const PromotionButtonContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  alignSelf: 'flex-end'
}))``

const PromotionButtonText = styled(DefaultButtonText)`
  margin-right: ${margin[marginSizes.small]};
  color: ${(props) => props.theme.buttonColor};
`

const IconForward = styled(IconBack)`
  transform: rotate(180deg);
`

// TODO: move to tool if there are other use case for this
const getChinesePercentageDiscountAmount = (amount) => {
  const reverse = 100 - amount
  return reverse % 10 === 0 ? reverse / 10 : Math.floor(reverse)
}

export const getPromotionAvailabilityText = ({
  t,
  promotion
}: {
  t: (string) => string
  promotion: {
    appliedOn: number
  }
}): string => {
  switch (promotion.appliedOn) {
    case promotionAppliedOnTypes.pickup:
      return t('Valid for Pickup only!')
    case promotionAppliedOnTypes.delivery:
      return t('Valid for Delivery only!')
    case promotionAppliedOnTypes.both:
      return t('Valid for Pickup and Delivery!')
  }
}

const getBannerHeaderText = ({
  t,
  resultAmountType,
  formattedDiscountAmount
}) => {
  return resultAmountType === amountTypes.percentage
    ? t('_percentageDiscountAmount_%', formattedDiscountAmount)
    : t('_dollarDiscountAmount_', formattedDiscountAmount)
}

/*  eslint-disable react/prop-types */
export const getPromotionBanner = ({
  t,
  promotion,
  formatMoney,
  PromotionBannerHeader = DefaultPromotionBannerHeader,
  PromotionBannerSubHeader = DefaultPromotionBannerSubHeader
}: {
  t: (string, params?) => string
  promotion: Promotion
  formatMoney: (number) => string
  PromotionBannerHeader?
  PromotionBannerSubHeader?
}): JSX.Element => {
  const percentageDiscountAmount = promotion.resultAmount
  const chinesePercentageDiscountAmount = getChinesePercentageDiscountAmount(
    promotion.resultAmount
  )
  const dollarDiscountAmount = formatMoney(promotion.resultAmount)
  const resultAmountType = promotion.resultAmountType
  const formattedDiscountAmount = {
    percentageDiscountAmount,
    chinesePercentageDiscountAmount,
    dollarDiscountAmount
  }
  switch (promotion.promotionResultType) {
    case promotionTypes.applySelectedItem:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '_percentageDiscountAmount_ Off Selected Item(s)!',
                  formattedDiscountAmount
                )
              : t(
                  '_dollarDiscountAmount_ Off Selected Item(s)!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.minimumOrder:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {t('_minOrderOverridenAmount_', {
              minOrderOverridenAmount:
                formattedDiscountAmount.dollarDiscountAmount
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {t(
              '_dollarDiscountAmount_ Minimum Order!',
              formattedDiscountAmount
            )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.totalOrder:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '_percentageDiscountAmount_ Off Total Order!',
                  formattedDiscountAmount
                )
              : t(
                  '_dollarDiscountAmount_ Off Total Order!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.freeItem:
      return (
        <PromotionBannerHeader>
          {t('Free Item!', formattedDiscountAmount)}
        </PromotionBannerHeader>
      )
    case promotionTypes.deliveryFree:
      const isFree = promotion.resultAmount === 0
      return isFree ? (
        <React.Fragment>
          <PromotionBannerHeader>{t('Free Delivery!')}</PromotionBannerHeader>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '_percentageDiscountAmount_ Off Delivery Charges!',
                  formattedDiscountAmount
                )
              : t(
                  '_dollarDiscountAmount_ Off Delivery Charges!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
    case promotionTypes.discountNextItem:
      return (
        <React.Fragment>
          <PromotionBannerHeader>
            {getBannerHeaderText({
              t,
              resultAmountType,
              formattedDiscountAmount
            })}
          </PromotionBannerHeader>
          <PromotionBannerSubHeader>
            {resultAmountType === amountTypes.percentage
              ? t(
                  '_percentageDiscountAmount_ Off Next Item!',
                  formattedDiscountAmount
                )
              : t(
                  '_dollarDiscountAmount_ Off Next Item!',
                  formattedDiscountAmount
                )}
          </PromotionBannerSubHeader>
        </React.Fragment>
      )
  }
}

/*  eslint-enable react/prop-types */

const PromotionCard = ({
  promotion,
  showDefaultImage,
  theme
}: {
  promotion: Promotion
  showDefaultImage: boolean
  theme: Record<string, unknown>
}) => {
  const { formatMoney } = useAppContext()
  const { t } = React.useContext(TranslationContext)
  return (
    <PromotionCardContainer showDefaultImage={showDefaultImage}>
      <PromotionCardWrapper>
        {showDefaultImage && (
          <PromotionBanner>
            {getPromotionBanner({ t, promotion, formatMoney })}
          </PromotionBanner>
        )}
        <PromotionContent>
          <PromotionText>{promotion.name}</PromotionText>
          <PromotionButtonContainer>
            <PromotionButtonText>{t('View Details')}</PromotionButtonText>
            <IconForward fill={theme.buttonColor} />
          </PromotionButtonContainer>
        </PromotionContent>
      </PromotionCardWrapper>
    </PromotionCardContainer>
  )
}

export default withTheme((props) => (<PromotionCard {...props} />))
